import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useState,
} from 'react';
import { InlineLoadingSuspenseBoundary } from './InlineLoadingSuspenseBoundary';
import { useTranslation } from 'react-i18next';
import { QueryErrorBoundary } from './QueryErrorBoundary';
import { SectionErrorFallbackWithReset } from './SectionErrorFallback';
import { Panel, PanelManager, PanelProps } from '@schuettflix/react-components';
import { cn } from '../utils/cn';

const WizardPanelContext = createContext<
    | {
          hasOpenedPanel: boolean;
          addOpenedPanel: (identifier: string) => void;
          removeOpenedPanel: (identifier: string) => void;
      }
    | undefined
>(undefined);
export function useWizardPanelContext() {
    const wizardPanelContext = useContext(WizardPanelContext);
    if (!wizardPanelContext) {
        throw new Error('useWizardPanelContext must be used within a Wizard');
    }
    return wizardPanelContext;
}
function WizardPanelContextProvider({ children }: PropsWithChildren) {
    const [openedPanels, setOpenedPanels] = useState<Set<string>>(new Set());
    const hasOpenedPanel = useMemo(() => openedPanels.size > 0, [openedPanels]);
    const addOpenedPanel = useCallback(
        (identifier: string) => {
            setOpenedPanels(prev => {
                const newSet = new Set(prev);
                newSet.add(identifier);
                return newSet;
            });
        },
        [setOpenedPanels]
    );
    const removeOpenedPanel = useCallback(
        (identifier: string) => {
            setOpenedPanels(prev => {
                const newSet = new Set(prev);
                newSet.delete(identifier);
                return newSet;
            });
        },
        [setOpenedPanels]
    );

    return (
        <WizardPanelContext.Provider value={{ hasOpenedPanel, addOpenedPanel, removeOpenedPanel }}>
            {children}
        </WizardPanelContext.Provider>
    );
}

export function Wizard({ children }: PropsWithChildren) {
    return (
        <WizardPanelContextProvider>
            <PanelManager className="max-h-[calc(100vh-120px)] max-w-[1936px] overflow-clip">
                <div className="flex w-full flex-1 items-start justify-center">{children}</div>
            </PanelManager>
        </WizardPanelContextProvider>
    );
}

export function WizardSections({ children }: PropsWithChildren) {
    return (
        <div className="sticky top-[96px] order-1 flex h-[calc(100vh-120px)] min-w-0 flex-grow basis-2/3 justify-center">
            {children}
        </div>
    );
}

export function WizardSidebar({ children }: PropsWithChildren) {
    const { t } = useTranslation();

    const { hasOpenedPanel } = useWizardPanelContext();
    const isVisible = !hasOpenedPanel;

    return (
        <div
            className={cn(
                'origin-right transition-all duration-300 ease-in-out',
                isVisible ? 'max-w-full opacity-100' : 'max-w-0 opacity-0',
                'sticky top-[96px] order-2 hidden h-[calc(100vh-120px)] basis-1/3 overflow-x-hidden lg:flex'
            )}
        >
            <div className="h-px w-6 flex-none" />
            <aside className="bg-surface flex-1 overflow-y-auto rounded">
                <header className="p-6">
                    <h3 className="font-numbers-md-strong">{t('wizard.summary')}</h3>
                </header>
                <div className="flex flex-col items-stretch gap-8 px-6 pb-6">{children}</div>
            </aside>
        </div>
    );
}

export function WizardSidebarSections({ children }: PropsWithChildren) {
    return <div className="flex flex-col items-stretch gap-2">{children}</div>;
}

type WizardSectionProps = PropsWithChildren & {
    title: string;
    headerAside?: React.ReactNode;
};

export function WizardClientSection({ children, title }: Pick<WizardSectionProps, 'title' | 'children'>) {
    return (
        <section className="flex w-full max-w-3xl flex-col gap-4">
            <header className="flex items-center justify-between py-6">
                <h2 className="font-headline-lg">{title}</h2>
            </header>
            <div>
                <InlineLoadingSuspenseBoundary className="p-[95px]">
                    <QueryErrorBoundary FallbackComponent={SectionErrorFallbackWithReset}>
                        {children}
                    </QueryErrorBoundary>
                </InlineLoadingSuspenseBoundary>
            </div>
        </section>
    );
}

export function WizardSection({ children, title, headerAside }: WizardSectionProps) {
    return (
        <section className="bg-surface shadow-low relative flex h-full min-w-0 flex-1 flex-col overflow-auto scroll-smooth rounded">
            <header className="bg-surface sticky inset-x-0 top-0 z-10 flex flex-none items-center justify-between p-6">
                <h2 className="font-numbers-md-strong">{title}</h2>
                {headerAside && <div>{headerAside}</div>}
            </header>
            <div className="flex flex-1 justify-center px-6">
                <InlineLoadingSuspenseBoundary className="p-[95px]">
                    <QueryErrorBoundary FallbackComponent={SectionErrorFallbackWithReset}>
                        {children}
                    </QueryErrorBoundary>
                </InlineLoadingSuspenseBoundary>
            </div>
        </section>
    );
}

type WizardFooterProps = PropsWithChildren & {
    className?: string;
};

export function WizardFooter({ className, children }: WizardFooterProps) {
    return (
        <footer
            className={cn(
                'bg-surface shadow-high-dark sticky bottom-0 z-10 -mx-6 flex justify-end px-6 py-4',
                className
            )}
        >
            {children}
        </footer>
    );
}

export function WizardPanel({
    className: classNameFromProps,
    footerPadding = 'sm',
    ...props
}: PropsWithChildren<PanelProps>) {
    const { addOpenedPanel, removeOpenedPanel } = useWizardPanelContext();
    const className = cn('@container', classNameFromProps);
    const id = useId();
    useEffect(() => {
        addOpenedPanel(id);
        return () => {
            removeOpenedPanel(id);
        };
    }, [addOpenedPanel, removeOpenedPanel, id]);
    return <Panel {...{ className, footerPadding, ...props }} />;
}
